<template>
  <div class="route-config-page" v-loading="loading">
    <div class="page-heading">
      <div>
        <div class="page-heading__title">{{ isEdit ? '编辑运输路线' : '新建运输路线' }}</div>
        <div class="page-heading__sub">维护起点、途经点和终点；完成地图规划后可作为运单节点模板使用。</div>
      </div>
      <el-button icon="el-icon-back" :disabled="saving" @click="backToList">返回列表</el-button>
    </div>

    <el-form v-loading="saving" :disabled="saving" ref="form" :model="form" :rules="rules" label-position="top" class="route-config-form">
      <section class="form-section route-basic-section">
        <div class="section-title">路线信息</div>
        <div class="basic-grid">
          <el-form-item label="路线名称" prop="routeName" required>
            <el-input v-model.trim="form.routeName" maxlength="100" placeholder="请输入路线名称" />
          </el-form-item>
          <el-form-item label="驾车策略">
            <el-select v-model="form.drivingStrategy" @change="clearPlan">
              <el-option label="速度优先" :value="0" />
              <el-option label="最少费用" :value="1" />
              <el-option label="最短路径" :value="2" />
              <el-option label="规避拥堵" :value="3" />
            </el-select>
          </el-form-item>
          <el-form-item label="路线状态">
            <el-switch v-model="routeEnabled" active-text="启用" inactive-text="禁用" />
          </el-form-item>
          <el-form-item label="规划里程（千米）">
            <el-input v-model.trim="form.totalDileage" placeholder="请输入里程" />
          </el-form-item>
          <el-form-item label="预计时长（分钟）">
            <el-input v-model.trim="plannedDurationMinutes" placeholder="请输入时长" />
          </el-form-item>
          <el-form-item label="地图规划">
            <el-button type="primary" icon="el-icon-guide" v-permission="isEdit ? 'route:edit' : 'route:add'" @click="goPlanner">地图规划</el-button>
          </el-form-item>
        </div>
        <div v-if="form.routeList" class="plan-summary">
          已选择规划路线：{{ mileageText(form.totalDileage) }} · {{ durationText(form.totalDuration) }}<span v-if="form.tolls !== null && form.tolls !== undefined"> · 收费 {{ Number(form.tolls).toFixed(2) }} 元</span>
          <el-button type="text" v-permission="isEdit ? 'route:edit' : 'route:add'" @click="goPlanner">重新规划</el-button>
        </div>
        <div v-else class="plan-tip">可先填写节点再进入地图规划；未添加途经点时，将直接按起点和终点规划。</div>
      </section>

      <section class="form-section node-section">
        <div class="section-title section-title--with-actions">
          <span>路线节点</span>
          <div class="section-actions">
            <el-button plain type="primary" size="small" icon="el-icon-plus" :disabled="form.waypoints.length >= 8" @click="addWaypoint">新增途经点</el-button>
            <span>最多 8 个途经点</span>
          </div>
        </div>
        <div class="node-section-tip">节点变更后可重新规划；“解析坐标”和“地图选点”互不影响，坐标会直接展示在输入框中。</div>

        <div class="route-node-table">
          <div class="route-node-table__head">
            <span>节点</span><span><i>*</i>省 / 市 / 区</span><span><i>*</i>详细地址 / 定位</span><span><i>*</i>坐标</span><span>联系人</span><span>联系电话</span><span>围栏半径</span><span>操作</span>
          </div>
          <div v-for="item in routeNodes" :key="item.key" class="route-node-row">
            <div class="route-node-row__type" :class="`route-node-row__type--${item.type}`">{{ item.title }}</div>
            <el-cascader v-model="item.node.areaCodes" :options="regionOptions" clearable placeholder="请选择地区" @change="onAreaChange(item.node)" />
            <div class="route-node-address">
              <address-book-input v-model="item.node.address" placeholder="输入地址名称可选择地址簿" @input="clearPlan" @select="applyAddressBook(item.node, $event)" />
              <div class="route-node-address__actions">
                <el-button type="text" @click="resolveNodeCoordinate(item.node)">解析坐标</el-button>
                <el-button type="text" @click="openLocationPicker(item.node)">地图选点</el-button>
              </div>
            </div>
            <coordinate-input v-model="item.node.lonLat" maxlength="40" clearable placeholder="经度,纬度" @change="clearPlan" />
            <el-input v-model.trim="item.node.contactName" maxlength="64" placeholder="联系人" />
            <el-input v-model.trim="item.node.contactPhone" maxlength="20" placeholder="联系电话" />
            <el-input-number v-model="item.node.radius" :min="50" :max="5000" :step="50" controls-position="right" @change="clearPlan" />
            <div class="route-node-row__actions">
              <template v-if="item.type === 'waypoint'">
                <el-button type="text" :disabled="item.index === 0" @click="moveWaypoint(item.index, -1)">上移</el-button>
                <el-button type="text" :disabled="item.index === form.waypoints.length - 1" @click="moveWaypoint(item.index, 1)">下移</el-button>
                <el-button type="text" class="danger-button" @click="removeWaypoint(item.index)">移除</el-button>
              </template>
            </div>
          </div>
        </div>
      </section>
    </el-form>

    <div class="form-actions">
      <el-button :disabled="saving" @click="backToList">取消</el-button>
      <el-button type="primary" :loading="saving" v-permission="isEdit ? 'route:edit' : 'route:add'" @click="submit">保存路线</el-button>
    </div>

    <waybill-location-picker
      :visible.sync="locationPickerVisible"
      :lon-lat="selectedNode ? selectedNode.lonLat : ''"
      :address="selectedNode ? selectedNode.address : ''"
      :area-codes="selectedNode ? selectedNode.areaCodes : []"
      :area-name="selectedNode ? getAreaName(selectedNode) : ''"
      @confirm="applyPickedLocation"
    />
  </div>
</template>

<script>
import { regionData } from 'element-china-area-data'
import { getRouteDetail, isRequestSuccess, saveRoute, updateRoute } from './lingdongApi'
import WaybillLocationPicker from './WaybillLocationPicker.vue'
import AddressBookInput from './AddressBookInput.vue'
import CoordinateInput from './CoordinateInput.vue'
import { setAmapSecurityConfig, loadAmapSDK } from '@/utils/amapConfig'
import { normalizeAreaCodes, getAreaName, stripAreaPrefix, buildFullAddress } from './locationArea'
import { gcj02PointToWgs84LonLat } from './coordinate'
import { confirmRoutePassage } from './routePassage'

const ROUTE_DRAFT_KEY = 'lingdong-route-draft'
const createNode = () => ({ key: `route-node-${Date.now()}-${Math.random()}`, areaCodes: [], address: '', lonLat: '', contactName: '', contactPhone: '', radius: 500, selectedAreaName: '' })
const createForm = () => ({ id: null, routeName: '', drivingStrategy: 0, status: 1, start: createNode(), end: createNode(), waypoints: [], routeList: '', totalDileage: null, totalDuration: null, tolls: null, tollDistance: null })

export default {
  name: 'RouteConfigForm',
  components: { WaybillLocationPicker, AddressBookInput, CoordinateInput },
  data() {
    return {
      loading: false,
      saving: false,
      submitRequestId: 0,
      firstActivated: true,
      autoRouteName: '',
      locationPickerVisible: false,
      selectedNode: null,
      regionOptions: regionData,
      form: createForm(),
      rules: { routeName: [{ required: true, message: '请输入路线名称', trigger: 'blur' }] }
    }
  },
  computed: {
    isEdit() { return Boolean(this.$route.params.id) },
    routeEnabled: {
      get() { return Number(this.form.status) === 1 },
      set(value) { this.form.status = value ? 1 : 0 }
    },
    plannedDurationMinutes: {
      get() { return this.form.totalDuration ? String(Math.ceil(Number(this.form.totalDuration) / 60)) : '' },
      set(value) {
        const minutes = Number(value)
        this.form.totalDuration = value === null || value === undefined || value === '' || !Number.isFinite(minutes) ? null : Math.round(minutes * 60)
      }
    },
    routeNodes() {
      return [
        { key: 'start', title: '起点', type: 'origin', node: this.form.start },
        ...this.form.waypoints.map((node, index) => ({ key: node.key, title: `途经点 ${index + 1}`, type: 'waypoint', index, node })),
        { key: 'end', title: '终点', type: 'target', node: this.form.end }
      ]
    }
  },
  async created() {
    if (this.restorePlannerDraft()) return
    sessionStorage.removeItem(ROUTE_DRAFT_KEY)
    if (this.isEdit) await this.loadDetail()
    else this.resetCreateForm()
  },
  activated() {
    if (this.firstActivated) {
      this.firstActivated = false
      return
    }
    if (this.restorePlannerDraft()) return
    sessionStorage.removeItem(ROUTE_DRAFT_KEY)
    if (this.isEdit) {
      this.loadDetail()
      return
    }
    this.resetCreateForm()
  },
  beforeRouteLeave(to, from, next) {
    this.invalidateSubmission()
    next()
  },
  deactivated() { this.invalidateSubmission() },
  beforeDestroy() { this.invalidateSubmission() },
  methods: {
    invalidateSubmission() {
      this.submitRequestId += 1
      this.saving = false
    },
    async loadDetail() {
      this.loading = true
      try {
        const res = await getRouteDetail(this.$route.params.id)
        if (!isRequestSuccess(res)) throw new Error(res.msg || '路线不存在')
        const data = res.data || {}
        this.form = {
          id: data.id,
          routeName: data.routeName || '',
          drivingStrategy: Number(data.drivingStrategy) || 0,
          status: data.status === undefined || data.status === null ? 1 : Number(data.status),
          start: this.normalizeNode({ areaCodes: data.startAreaCodes, areaName: data.startAreaName, address: data.startAddress, lonLat: data.startLonlat, contactName: data.startContactName, contactPhone: data.startContactPhone, radius: data.startRadius }),
          end: this.normalizeNode({ areaCodes: data.endAreaCodes, areaName: data.endAreaName, address: data.endAddress, lonLat: data.endLonlat, contactName: data.endContactName, contactPhone: data.endContactPhone, radius: data.endRadius }),
          waypoints: this.parseWaypoints(data.waypoints),
          routeList: data.routeList || '',
          totalDileage: data.totalDileage,
          totalDuration: data.totalDuration,
          tolls: data.tolls,
          tollDistance: data.tollDistance
        }
        this.autoRouteName = this.getDefaultRouteName()
        this.clearFormValidate()
      } catch (e) {
        this.$message.error(e.msg || e.message || '加载路线失败')
        this.backToList()
      } finally {
        this.loading = false
      }
    },
    restorePlannerDraft() {
      if (this.$route.query.fromPlanner !== '1') return false
      try {
        const value = sessionStorage.getItem(ROUTE_DRAFT_KEY)
        if (!value) return false
        const draft = JSON.parse(value)
        if ((this.isEdit && Number(draft.id) !== Number(this.$route.params.id)) || (!this.isEdit && draft.id)) return false
        this.form = this.normalizeForm(draft)
        this.autoRouteName = this.getDefaultRouteName()
        sessionStorage.removeItem(ROUTE_DRAFT_KEY)
        this.clearFormValidate()
        return true
      } catch (e) {
        sessionStorage.removeItem(ROUTE_DRAFT_KEY)
        return false
      }
    },
    resetCreateForm() {
      this.form = createForm()
      this.autoRouteName = ''
      this.selectedNode = null
      this.clearFormValidate()
    },
    clearFormValidate() {
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    normalizeForm(value) {
      const result = { ...createForm(), ...value }
      result.start = this.normalizeNode(value.start)
      result.end = this.normalizeNode(value.end)
      result.waypoints = Array.isArray(value.waypoints) ? value.waypoints.map(item => this.normalizeNode(item)) : []
      return result
    },
    normalizeNode(node = {}) {
      const result = { ...createNode(), ...node }
      result.areaCodes = normalizeAreaCodes(result.areaCodes)
      result.lonLat = result.lonLat || result.lonlat || ''
      result.radius = Number(result.radius) || 500
      result.selectedAreaName = this.getAreaName(result) || result.areaName || result.selectedAreaName || ''
      result.address = stripAreaPrefix(result.address, result.selectedAreaName)
      return result
    },
    parseWaypoints(value) {
      try {
        const items = JSON.parse(value || '[]')
        return Array.isArray(items) ? items.map(item => this.normalizeNode(item)) : []
      } catch (e) {
        return []
      }
    },
    addWaypoint() {
      if (this.form.waypoints.length >= 8) return
      this.form.waypoints.push(createNode())
      this.clearPlan()
    },
    removeWaypoint(index) { this.form.waypoints.splice(index, 1); this.clearPlan() },
    moveWaypoint(index, direction) {
      const target = index + direction
      if (target < 0 || target >= this.form.waypoints.length) return
      const item = this.form.waypoints[index]
      this.$set(this.form.waypoints, index, this.form.waypoints[target])
      this.$set(this.form.waypoints, target, item)
      this.clearPlan()
    },
    onAreaChange(node) {
      const oldAreaName = node.selectedAreaName || ''
      node.address = stripAreaPrefix(node.address, oldAreaName)
      node.selectedAreaName = this.getAreaName(node)
      this.applyDefaultRouteName()
      this.clearPlan()
    },
    applyAddressBook(node, addressBook) {
      node.areaCodes = normalizeAreaCodes(addressBook.areaCodes)
      node.selectedAreaName = this.getAreaName(node) || addressBook.areaName || ''
      node.address = stripAreaPrefix(addressBook.address, node.selectedAreaName)
      if (addressBook.longitude !== null && addressBook.longitude !== undefined && addressBook.latitude !== null && addressBook.latitude !== undefined) {
        node.lonLat = `${addressBook.longitude},${addressBook.latitude}`
      }
      node.contactName = addressBook.contactName || ''
      node.contactPhone = addressBook.contactPhone || ''
      this.applyDefaultRouteName()
      this.clearPlan()
    },
    getDefaultRouteName() {
      const startAreaName = this.getAreaName(this.form.start)
      const endAreaName = this.getAreaName(this.form.end)
      return startAreaName && endAreaName ? `${startAreaName}到${endAreaName}` : ''
    },
    applyDefaultRouteName() {
      const defaultRouteName = this.getDefaultRouteName()
      if (!defaultRouteName || (this.form.routeName && this.form.routeName !== this.autoRouteName)) return
      this.form.routeName = defaultRouteName
      this.autoRouteName = defaultRouteName
    },
    openLocationPicker(node) { this.selectedNode = node; this.locationPickerVisible = true },
    applyPickedLocation(location) {
      if (!this.selectedNode) return
      this.selectedNode.lonLat = location.lonLat
      if (Array.isArray(location.areaCodes) && location.areaCodes.length) this.selectedNode.areaCodes = normalizeAreaCodes(location.areaCodes)
      this.selectedNode.selectedAreaName = this.getAreaName(this.selectedNode) || location.areaName || ''
      if (location.address) this.selectedNode.address = stripAreaPrefix(location.address, this.selectedNode.selectedAreaName)
      this.applyDefaultRouteName()
      this.selectedNode = null
      this.clearPlan()
    },
    async resolveNodeCoordinate(node) {
      const address = this.getFullAddress(node)
      if (!address) { this.$message.warning('请先选择省市区或填写详细地址'); return }
      try {
        setAmapSecurityConfig()
        await loadAmapSDK(['AMap.Geocoder'])
        const geocode = (query) => new Promise((resolve) => {
          new window.AMap.Geocoder().getLocation(query, (status, result) => {
            if (status !== 'complete' || !result.geocodes || !result.geocodes.length) { resolve(null); return }
            const location = result.geocodes[0].location
            resolve([location.getLng(), location.getLat()])
          })
        })
        let point = await geocode(address)
        const areaName = this.getAreaName(node)
        if (!point && areaName && areaName !== address) point = await geocode(areaName)
        if (!point) { this.$message.warning('未解析到坐标，请使用地图选点'); return }
        node.lonLat = gcj02PointToWgs84LonLat(point[0], point[1])
        this.clearPlan()
        this.$message.success('坐标已解析')
      } catch (e) {
        this.$message.warning('坐标解析失败，请使用地图选点')
      }
    },
    getAreaName(node) { return getAreaName(node.areaCodes) },
    getFullAddress(node) {
      return buildFullAddress(node.areaCodes, node.address)
    },
    parseLonLat(value) {
      const values = String(value || '').trim().split(',').map(Number)
      return values.length === 2 && values.every(Number.isFinite) && Math.abs(values[0]) <= 180 && Math.abs(values[1]) <= 90 ? values : null
    },
    validateNode(node, label) {
      if (!this.getFullAddress(node)) return `${label}地址不能为空`
      if (!this.parseLonLat(node.lonLat)) return `${label}坐标格式错误，请通过地图选点或填写经度,纬度`
      return ''
    },
    validateNodes() {
      const startError = this.validateNode(this.form.start, '起点')
      if (startError) return startError
      for (let index = 0; index < this.form.waypoints.length; index += 1) {
        const error = this.validateNode(this.form.waypoints[index], `途经点${index + 1}`)
        if (error) return error
      }
      return this.validateNode(this.form.end, '终点')
    },
    clearPlan() {
      this.form.routeList = ''
      this.form.tolls = null
      this.form.tollDistance = null
    },
    goPlanner() {
      sessionStorage.setItem(ROUTE_DRAFT_KEY, JSON.stringify(this.form))
      this.$router.push({ name: 'LingDongRoutePlannerName', query: { routeId: this.form.id || '' } })
    },
    convertNode(node) {
      return {
        areaCodes: node.areaCodes.join(','),
        areaName: this.getAreaName(node),
        address: String(node.address || '').trim(),
        lonLat: node.lonLat.trim(),
        radius: node.radius,
        contactName: node.contactName || '',
        contactPhone: node.contactPhone || ''
      }
    },
    buildPayload() {
      const start = this.convertNode(this.form.start)
      const end = this.convertNode(this.form.end)
      return {
        id: this.form.id,
        routeName: this.form.routeName,
        drivingStrategy: this.form.drivingStrategy,
        status: this.form.status,
        startAddress: start.address,
        startAreaCodes: start.areaCodes,
        startAreaName: start.areaName,
        startContactName: start.contactName,
        startContactPhone: start.contactPhone,
        startLonlat: start.lonLat,
        startRadius: start.radius,
        endAddress: end.address,
        endAreaCodes: end.areaCodes,
        endAreaName: end.areaName,
        endContactName: end.contactName,
        endContactPhone: end.contactPhone,
        endLonlat: end.lonLat,
        endRadius: end.radius,
        waypoints: JSON.stringify(this.form.waypoints.map(item => this.convertNode(item))),
        routeList: this.form.routeList,
        totalDileage: this.form.totalDileage,
        totalDuration: this.form.totalDuration,
        tolls: this.form.tolls,
        tollDistance: this.form.tollDistance,
        isDefault: 1
      }
    },
    async submit() {
      if (this.saving) return
      this.saving = true
      const requestId = ++this.submitRequestId
      const isCurrent = () => requestId === this.submitRequestId
      try {
        try { await this.$refs.form.validate() } catch (e) { return }
        if (!isCurrent()) return
        const error = this.validateNodes()
        if (error) { this.$message.warning(error); return }
        if (!this.form.routeList) { this.$message.warning('请先完成地图规划并选择路线'); return }
        // 校验和保存共用同一快照，避免确认期间使用不同节点或路线。
        const payload = this.buildPayload()
        const save = this.isEdit ? updateRoute : saveRoute
        const nodes = [
          { code: 'start', name: `起点：${payload.startAddress}`, lonLat: payload.startLonlat, radius: payload.startRadius },
          ...JSON.parse(payload.waypoints).map((node, index) => ({ code: `waypoint-${index + 1}`, name: `途经点 ${index + 1}：${node.address}`, lonLat: node.lonLat, radius: node.radius })),
          { code: 'end', name: `终点：${payload.endAddress}`, lonLat: payload.endLonlat, radius: payload.endRadius }
        ]
        if (!await confirmRoutePassage(this, { routeList: payload.routeList, nodes, fenceCodes: [] }, isCurrent) || !isCurrent()) return
        const res = await save(payload)
        if (!isCurrent()) return
        if (!isRequestSuccess(res)) throw new Error(res.msg || '保存失败')
        sessionStorage.removeItem(ROUTE_DRAFT_KEY)
        this.$message.success('路线保存成功')
        this.$router.push({ name: 'LingDongRouteManageName' })
      } catch (e) {
        if (isCurrent()) this.$message.error(e.msg || e.message || '保存失败')
      } finally {
        if (isCurrent()) this.saving = false
      }
    },
    mileageText(value) { return value === null || value === undefined || value === '' ? '未规划' : `${Number(value).toFixed(2)} 公里` },
    durationText(value) {
      if (!value) return '未规划'
      const seconds = Number(value)
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.ceil((seconds % 3600) / 60)
      return hours ? `${hours}小时${minutes}分` : `${minutes}分`
    },
    backToList() {
      if (this.saving) return
      sessionStorage.removeItem(ROUTE_DRAFT_KEY)
      this.$router.push({ name: 'LingDongRouteManageName' })
    }
  }
}
</script>

<style scoped>
.route-config-page { min-height: 100%; padding: 20px 24px 86px; box-sizing: border-box; background: #f5f7fb; }
.page-heading { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.page-heading__title { color: #172b4d; font-size: 20px; font-weight: 600; line-height: 28px; }
.page-heading__sub { margin-top: 4px; color: #8a94a6; font-size: 13px; }
.route-config-form { max-width: 1540px; }
.form-section { margin-bottom: 16px; padding: 20px 22px; border-radius: 8px; background: #fff; box-shadow: 0 2px 8px rgba(27, 49, 84, .05); }
.section-title { margin-bottom: 20px; color: #1f2d3d; font-size: 17px; font-weight: 600; }
.section-title::before { display: inline-block; width: 4px; height: 18px; margin-right: 9px; vertical-align: -3px; border-radius: 2px; background: #2f78ed; content: ''; }
.section-title--with-actions { display: flex; align-items: center; justify-content: space-between; }
.section-actions { display: flex; align-items: center; gap: 12px; color: #98a2b3; font-size: 13px; font-weight: normal; }
.basic-grid { display: grid; grid-template-columns: 1.6fr 1fr .75fr 1fr 1fr 120px; gap: 0 18px; }
.basic-grid ::v-deep .el-form-item { margin-bottom: 0; }
.basic-grid ::v-deep .el-select { width: 100%; }
.basic-grid ::v-deep .el-form-item__label, .route-node-row ::v-deep .el-form-item__label { padding-bottom: 7px; color: #475467; line-height: 18px; }
.plan-summary, .plan-tip { display: flex; align-items: center; gap: 7px; margin-top: 16px; padding: 10px 12px; border-radius: 5px; font-size: 13px; }
.plan-summary { background: #f0f9eb; color: #529b2e; }
.plan-summary .el-button { margin-left: auto; }
.plan-tip { background: #f4f7fb; color: #8a94a6; }
.node-section-tip { margin: -9px 0 12px; color: #98a2b3; font-size: 13px; }
.route-node-table { border: 1px solid #e7ebf2; border-radius: 6px; overflow: hidden; }
.route-node-table__head, .route-node-row { display: grid; grid-template-columns: 70px minmax(160px, 1.15fr) minmax(290px, 2.2fr) 145px minmax(110px, .8fr) minmax(110px, .8fr) 112px 145px; gap: 8px; align-items: center; }
.route-node-table__head { min-height: 38px; padding: 0 12px; background: #f8fafc; color: #667085; font-size: 12px; }
.route-node-table__head i { margin-right: 3px; color: #f56c6c; font-style: normal; }
.route-node-row { min-height: 64px; padding: 10px 12px; border-top: 1px solid #eef1f6; }
.route-node-row:first-of-type { border-top: 0; }
.route-node-row__type { display: inline-flex; align-items: center; justify-content: center; min-height: 32px; border-radius: 4px; color: #344054; background: #f8fafc; font-size: 13px; font-weight: 600; }
.route-node-row__type--origin { color: #067647; background: #e8f7ef; }
.route-node-row__type--waypoint { color: #1d5fca; background: #eaf2ff; }
.route-node-row__type--target { color: #b42318; background: #fef0f0; }
.route-node-row ::v-deep .el-cascader, .route-node-row ::v-deep .el-input-number { width: 100%; }
.route-node-row ::v-deep .el-input__inner { padding-right: 8px; font-size: 12px; }
.route-node-address { display: flex; align-items: center; gap: 7px; min-width: 0; }
.route-node-address ::v-deep .el-autocomplete, .route-node-address ::v-deep .el-input { flex: 1; min-width: 0; }
.route-node-address__actions { display: flex; flex: 0 0 auto; align-items: center; gap: 7px; white-space: nowrap; }
.route-node-address__actions .el-button { margin: 0; padding: 2px 0; }
.route-node-row__actions { display: flex; flex-wrap: wrap; align-items: center; gap: 3px 8px; }
.route-node-row__actions .el-button { margin: 0; padding: 2px 0; }
.danger-button { color: #e64545; }
.form-actions { position: fixed; right: 0; bottom: 0; left: 0; z-index: 20; display: flex; justify-content: center; gap: 12px; padding: 14px; border-top: 1px solid #e6eaf0; background: rgba(255, 255, 255, .96); box-shadow: 0 -2px 10px rgba(31, 45, 61, .05); }
@media (max-width: 1380px) { .basic-grid { grid-template-columns: repeat(3, minmax(200px, 1fr)); row-gap: 14px; } .route-node-table { overflow-x: auto; } .route-node-table__head, .route-node-row { min-width: 1250px; } }
@media (max-width: 900px) { .route-config-page { min-width: 900px; } }
</style>
